const ACTION_KEYWORDS = new Set(['create', 'edit', 'detail', 'update', 'view']);

/**
 * Builds a short VS Code-style tab title from the current pathname.
 * Examples:
 *  /master/users -> Users
 *  /master/users/create -> Users · Create
 *  /master/users/edit/abc -> Users · Edit
 *  /master/users/detail/abc/1 -> Users · Detail
 */
export function getWorkspaceTabTitle(pathname: string): string {
  const segments = pathname.split('/').filter(Boolean);

  let actionLabel: string | null = null;
  const resourceSegments: string[] = [];

  for (const segment of segments) {
    const lower = segment.toLowerCase();
    if (ACTION_KEYWORDS.has(lower)) {
      actionLabel = capitalize(lower);
      break;
    }
    // skip raw ids (uuids / numeric) after an action would already have broken above
    if (/^[0-9a-f-]{8,}$/i.test(segment) || /^\d+$/.test(segment)) {
      continue;
    }
    resourceSegments.push(segment);
  }

  // Prefer last meaningful resource segment (e.g. users under master)
  const resource =
    resourceSegments.length > 0
      ? formatSegment(resourceSegments[resourceSegments.length - 1])
      : formatSegment(segments[0] ?? '');

  return actionLabel ? `${resource} · ${actionLabel}` : resource;
}

function formatSegment(segment: string): string {
  return segment
    .split(/[-_]/)
    .filter(Boolean)
    .map(capitalize)
    .join(' ');
}

function capitalize(value: string): string {
  if (!value) return value;
  return value.charAt(0).toUpperCase() + value.slice(1);
}

/** One tab per route path; query-string changes update the existing tab. */
export function getWorkspaceTabKey(pathname: string, _search: string = ''): string {
  return pathname;
}

const TAB_ICONS: { prefix: string; icon: string }[] = [
  { prefix: '/dashboard', icon: 'uil-home-alt' },

  { prefix: '/master/users', icon: 'uil-user' },
  { prefix: '/master/roles', icon: 'uil-shield' },
  { prefix: '/master/currencies', icon: 'uil-dollar-sign' },
  { prefix: '/master/base-rates', icon: 'uil-graph-bar' },
  { prefix: '/master/holidays', icon: 'uil-calendar-alt' },
  { prefix: '/master/holiday-batch', icon: 'uil-schedule' },
  { prefix: '/master/branch', icon: 'uil-building' },
  { prefix: '/master/country', icon: 'uil-globe' },
  { prefix: '/master/state', icon: 'uil-map' },
  { prefix: '/master/city', icon: 'uil-map-marker' },
  { prefix: '/master/relationshipManagers', icon: 'uil-user-square' },
  { prefix: '/master/lov', icon: 'uil-list-ul' },
  { prefix: '/master/product-type', icon: 'uil-tag-alt' },
  { prefix: '/master/glaccount', icon: 'uil-book-open' },
  { prefix: '/master/co-lender-financiers', icon: 'uil-briefcase' },
  { prefix: '/master/system-parameters', icon: 'uil-cog' },
  { prefix: '/master', icon: 'uil-box' },

  { prefix: '/registration/customers', icon: 'uil-building' },
  { prefix: '/registration/customer-cic-reporting', icon: 'uil-file-check-alt' },
  { prefix: '/registration/limit-config-v2', icon: 'uil-sliders-v' },
  { prefix: '/registration/limit-config', icon: 'uil-sliders-v' },
  { prefix: '/registration/programme', icon: 'uil-layer-group' },
  { prefix: '/registration/buyer-seller', icon: 'uil-exchange' },
  { prefix: '/registration', icon: 'uil-document-layout-center' },

  { prefix: '/charges/receivable-charge', icon: 'uil-money-withdraw' },
  { prefix: '/charges/payable-charge', icon: 'uil-money-insert' },
  { prefix: '/charges/charge-settlement', icon: 'uil-check-circle' },
  { prefix: '/charges', icon: 'uil-receipt' },

  { prefix: '/transaction/instrument-batch-upload', icon: 'uil-file-upload-alt' },
  { prefix: '/transaction/instrument-batch', icon: 'uil-files-landscapes' },
  { prefix: '/transaction/instrument-balances-daily-history', icon: 'uil-history' },
  { prefix: '/transaction/instrument', icon: 'uil-invoice' },
  { prefix: '/transaction/staging-batch', icon: 'uil-processor' },
  { prefix: '/transaction/staging-instrument', icon: 'uil-clipboard-alt' },
  { prefix: '/transaction/payment-batch', icon: 'uil-money-stack' },
  { prefix: '/transaction/payment-instrument', icon: 'uil-bill' },
  { prefix: '/transaction/interest-accrual', icon: 'uil-clock' },
  { prefix: '/transaction/daily-balances', icon: 'uil-wallet' },
  { prefix: '/transaction/disbursement-authorization', icon: 'uil-lock-access' },
  { prefix: '/transaction', icon: 'uil-clipboard-alt' },

  { prefix: '/repayment/repayment-request', icon: 'uil-sync' },
  { prefix: '/repayment/repayment-intrument-bulk-request', icon: 'uil-layer-group' },
  { prefix: '/repayment/excess-money-settlements', icon: 'uil-balance-scale' },
  { prefix: '/repayment/excess-money', icon: 'uil-coins' },
  { prefix: '/repayment/excess-refund', icon: 'uil-redo' },
  { prefix: '/repayment', icon: 'uil-usd-circle' },

  { prefix: '/report', icon: 'uil-file-chart' },
  { prefix: '/system-info', icon: 'uil-rss' },
];

export function getWorkspaceTabIcon(pathname: string): string {
  const match = TAB_ICONS.filter(
    (item) => pathname === item.prefix || pathname.startsWith(`${item.prefix}/`)
  ).sort((a, b) => b.prefix.length - a.prefix.length)[0];

  return match?.icon ?? 'uil-file-alt';
}

/**
 * Parent listing path for create/edit/detail/update/view routes.
 * Examples:
 *  /master/users/detail/abc/1 -> /master/users
 *  /transaction/instrument/detail/xyz -> /transaction/instrument
 * Returns null when the path has no action segment (already a list, or unknown).
 */
export function getModuleListPath(pathname: string): string | null {
  const segments = pathname.split('/').filter(Boolean);
  const actionIndex = segments.findIndex((segment) => ACTION_KEYWORDS.has(segment.toLowerCase()));
  if (actionIndex <= 0) {
    return null;
  }
  return `/${segments.slice(0, actionIndex).join('/')}`;
}
